/**
 * @file Feature-highlighter.tsx
 * @module FeatureHighlighter
 * @description Karšu pārlūka objektu izcēlums (highlight) zem kursora, izmantojot
 *              canvas pieeju. Izveido ārpusekrāna karti, lai renderētu atsevišķus
 *              objektus izcēluma pikselī, tad piemēro iegūto stilu hover izcēlumam.
 *              Darbojas gan ar VectorTile, gan ar parasto Vector objektiem.
 */

import { Feature, Map, MapBrowserEvent } from "ol";
import { setFeatureState } from "ol-mapbox-style";
import RenderFeature from "ol/render/Feature";
import { useEffect, useRef } from "react";
import { Icon, Style } from "ol/style";
import { getCMSHighlight, getCMSMeteo, getPointHighlight } from "./Highlight-style";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { findStyle } from "../data-layers/layer-switcher/Cluster/Cooler-cluster-group";
import { LineString, Point } from "ol/geom";
import { getStyle } from "./overlay/Overlay";
import { FeatureLike } from "ol/Feature";
import { FEATURE_LIST } from "../data-layers/Data-layers-new-layer-manager";
import { getLineHighlight, getPointStyle } from "../data-layers/Style-generator";
import { getStyleItems, StyleLayerItem } from "../data-layers/Style-layer.repository";
import { take } from "rxjs";
import { LAYER_SET_SELECTED$ } from "./layer-set/Layerset-wrapper";
import { Pixel } from "ol/pixel";

/** Dedicated vector layer for rendering highlight overlays on hovered features */
export const HIGHLIGHT_LAYER = new VectorLayer({
	properties: { name: "Highlight", id: "highlight" },
	source: new VectorSource(),
});

const getCursorRelativePosition = (map: Map, event: MapBrowserEvent<any>, feature: FeatureLike) => {
	const geometry = feature.getGeometry();
	if (!(geometry instanceof Point)) return null;

	const featureCenter = geometry.getCoordinates();
	const centerPixel = map.getPixelFromCoordinate(featureCenter); // Dynamically compute feature pixel position
	const cursorPixel = event.pixel; // Cursor position in pixel coordinates

	const relativeX = cursorPixel[0] - centerPixel[0];
	const relativeY = cursorPixel[1] - centerPixel[1];

	return { relativeX, relativeY };
};

const isCursorInStyleBounds = (relativePosition: { relativeX: number; relativeY: number }, bounds: { minX: number; maxX: number; minY: number; maxY: number }) => {
	const { relativeX, relativeY } = relativePosition;
	return relativeX >= bounds.minX && relativeX <= bounds.maxX && relativeY >= bounds.minY && relativeY <= bounds.maxY;
};

/**
 * Determines which sub-style (camera or meteo station) applies based on
 * where the cursor is positioned relative to the combined CMS/camera icon.
 * CMS stations show a camera icon on top and a temperature badge below;
 * this function uses pixel-space bounds to determine which part was hovered.
 *
 * @param map - OpenLayers map instance
 * @param event - Mouse event for cursor position
 * @param feature - The hovered feature
 * @param options.returnStyleDef - If true, returns the style definition object instead of OL Style
 * @param options.overrideDetermined - If true, swaps the camera/meteo detection regions
 */
export const determineHoveredStyle = (
    map: Map,
    event: MapBrowserEvent<any>,
    feature: Feature,
    options?: { returnStyleDef?: boolean; overrideDetermined?: boolean },
): any => {
    const { returnStyleDef, overrideDetermined } = { ...options };
    let styleItems: StyleLayerItem[] | undefined;
    getStyleItems()
        .pipe(take(1))
        .subscribe((sl) => (styleItems = sl.flatMap((s) => s.layers)));

    const relativePosition = getCursorRelativePosition(map, event, feature);
    if (!relativePosition) return null;

    const cameraBounds = { minX: -16, maxX: 16, minY: -10, maxY: 16 };
    const meteoBounds = { minX: -16, maxX: 16, minY: -32, maxY: -10 };

    const hasMeteo = feature.get('t1') != null;

    const cameraStyle = () => {
        const style = styleItems!.find((sl) => ["Kameras", "Cameras", "Kameras un meteostacijas"].includes(sl.name))!;
        const point = style.points[0];
        if (["Ziemas", "Winter"].find((v) => (LAYER_SET_SELECTED$.value?.name ?? "").includes(v))) {
            point.piktogram = point.piktogram.replace("c_cctv_icon_02.svg", "c_cctv_icon_01.svg");
        } else {
            point.piktogram = point.piktogram.replace("c_cctv_icon_01.svg", "c_cctv_icon_02.svg");
        }
        return returnStyleDef ? point : getPointStyle(point);
    };

    const inCameraBounds = overrideDetermined
        ? isCursorInStyleBounds(relativePosition, meteoBounds)
        : isCursorInStyleBounds(relativePosition, cameraBounds);

    const inMeteoBounds = overrideDetermined
        ? isCursorInStyleBounds(relativePosition, cameraBounds)
        : isCursorInStyleBounds(relativePosition, meteoBounds);

    if (inCameraBounds) {
        return cameraStyle();
    } else if (inMeteoBounds) {
        if (!hasMeteo) return cameraStyle();
        const style = styleItems!.find((sl) => ["Meteostacija", "Weather stations", "Meteostacijas", "Weather station"].includes(sl.name))!;
        return returnStyleDef ? style.points[0] : findStyle({ table: "" }, feature, style.points[0]);
    }

    return cameraStyle();
};

/**
 * Component that manages hover-based feature highlighting on the map.
 * Adds the HIGHLIGHT_LAYER to the map and registers a pointermove listener
 * that clones hovered features with highlight styles. Handles point features,
 * linked line features, and tile-boundary edge cases.
 *
 * @param map - OpenLayers map instance
 */
export const FeatureHighlighter = ({ map }: { map: Map }) => {
	const features = useRef<(Feature | RenderFeature)[]>([]);

	useEffect(() => {
		if (
			!map ||
			map
				.getLayers()
				.getArray()
				.find((l) => l.get("id") === "highlight")
		)
			return;

		map.addLayer(HIGHLIGHT_LAYER);
	}, [map]);

	useEffect(() => {
		if (!map) return;

		const removeCurrHighlight = () => {
			HIGHLIGHT_LAYER.getSource()?.clear(true);
			features.current.forEach((f) => {
				const props = f.getProperties();
				if (props) {
					const layer = map.getAllLayers().find((l) => l.get("table") === props["internal"]["table"] && l.get("mapbox-source"));
					if (layer) {
						setFeatureState(
							layer as any,
							{ id: (props["linked_to"] ?? props["kafka_id"] ?? props["id"]) + "-" + props["layer"] + "-LineString", source: props["layer"] },
							undefined,
						);
						setFeatureState(
							layer as any,
							{ id: (props["linked_to"] ?? props["kafka_id"] ?? props["id"]) + "-" + props["layer"] + "-MultiLineString", source: props["layer"] },
							undefined,
						);
					}
				}
			});
			features.current = [];
		};

		const getTileNeighborPixels = (map: Map, pixel: Pixel): [number, number][] => {
			const coordinate = map.getCoordinateFromPixel(pixel);
			const view = map.getView();
			const resolution = view.getResolution()!;
			const tileSize = 256;
			const tileResolution = tileSize * resolution; // size of one tile in map units

			// Find which tile this coordinate falls in
			const tileX = Math.floor(coordinate[0] / tileResolution);
			const tileY = Math.floor(coordinate[1] / tileResolution);

			// Position within the current tile (0-1)
			const fracX = coordinate[0] / tileResolution - tileX;
			const fracY = coordinate[1] / tileResolution - tileY;

			const BOUNDARY_THRESHOLD = 0.06; // ~16px worth as fraction of tile
			const extra: [number, number][] = [];

			const sampleNeighbor = (dx: number, dy: number) => {
				// Sample point just inside the neighboring tile (5% in from its edge)
				const neighborCoord: [number, number] = [
					(tileX + dx + (dx === 1 ? 0.05 : dx === -1 ? 0.95 : fracX)) * tileResolution,
					(tileY + dy + (dy === 1 ? 0.05 : dy === -1 ? 0.95 : fracY)) * tileResolution,
				];
				const px = map.getPixelFromCoordinate(neighborCoord) as [number, number];
				if (px) extra.push(px);
			};

			if (fracX < BOUNDARY_THRESHOLD) sampleNeighbor(-1, 0);
			if (fracX > 1 - BOUNDARY_THRESHOLD) sampleNeighbor(1, 0);
			if (fracY < BOUNDARY_THRESHOLD) sampleNeighbor(0, -1);
			if (fracY > 1 - BOUNDARY_THRESHOLD) sampleNeighbor(0, 1);

			// Corners
			if (fracX < BOUNDARY_THRESHOLD && fracY < BOUNDARY_THRESHOLD) sampleNeighbor(-1, -1);
			if (fracX > 1 - BOUNDARY_THRESHOLD && fracY < BOUNDARY_THRESHOLD) sampleNeighbor(1, -1);
			if (fracX < BOUNDARY_THRESHOLD && fracY > 1 - BOUNDARY_THRESHOLD) sampleNeighbor(-1, 1);
			if (fracX > 1 - BOUNDARY_THRESHOLD && fracY > 1 - BOUNDARY_THRESHOLD) sampleNeighbor(1, 1);

			return extra;
		};

		const keyEvent = map.on("pointermove", (e) => {
			const pixelsToCheck: [number, number][] = [e.pixel as [number, number]];
			pixelsToCheck.push(...getTileNeighborPixels(map, e.pixel));

			const featuresFound: FeatureLike[] = [];
			const seenIds = new Set<string | number>();

			for (const px of pixelsToCheck) {
				map.getFeaturesAtPixel(px, { hitTolerance: 0 }).forEach((f) => {
					const id = f.getId();
					if (id != null && !seenIds.has(id)) {
						seenIds.add(id);
						featuresFound.push(f);
					}
				});
			}

			const [feat] = featuresFound.filter((f) => f.get("layer") !== "public.caps_kilometermarker");

			if (feat && features.current.find((f) => f.getId() === feat.getId())) return;
			if (feat && !feat.get("features") && feat.getProperties()["internal"]) {
				removeCurrHighlight();
				const highlightPoint = (feature: FeatureLike) => {
					if (feature instanceof Feature && !features.current.find((f) => f.getId() === feature?.getId()) && feature.getGeometry() instanceof Point) {
						const styleDef = getStyle(feature, feature.get("internal"));

						let style: Style[];
						let useAltHighlight = true;

						if (
							(feature.get("layer").includes("kafkamessages_cms") || feature.get("layer").includes("public.kafkamessages_camera")) &&
							feature.get("status") === "active"
						) {
							style = determineHoveredStyle(map, e, feature)!;
							if (style && (style[0]?.getImage() as any).iconImage_.src_.includes("c_cms_temperatura")) {
								useAltHighlight = false;
							}
						} else {
							const styleDefAlt = { ...styleDef };
							if (styleDefAlt?.piktogram?.includes("c_tonnas_default")) {
								const mass = feat.get("mass_restrictions");
								styleDefAlt.piktogram = [
									"3.5",
									"4",
									"4.5",
									"5",
									"5.5",
									"6",
									"6.5",
									"7",
									"7.5",
									"8",
									"8.5",
									"9",
									"9.5",
									...Array.from({ length: 43 }, (_, i) => `${i + 10}`),
								].includes(mass)
									? styleDefAlt.piktogram.replace("/icons/c_tonnas_default", "/tonnas/c_tonnas_" + mass)
									: styleDefAlt.piktogram.replace("/icons/c_tonnas_default", "/icons/c_icons_with_white_circle_12");
							}
							style = findStyle({ table: feature.get("layer").replace("public.", "") }, feature, styleDefAlt);
						}

						const clone = feature.clone();
						//@ts-expect-error
						clone.id_ = feature?.getId();
						let square = false;
						//@ts-expect-error
						const url = (style?.[0]?.getImage() as Icon)?.iconImage_?.src_?.split("/") as string[];
						if (url && url[url.length - 1].split("_")[0] === "s") {
							square = true;
						}

						const newStyle = [...style, useAltHighlight ? getPointHighlight(square) : getCMSHighlight()];

						if (style && (style[0]?.getImage() as any).iconImage_.src_.includes("c_cms_temperatura")) {
							newStyle.push(getCMSMeteo(feat.get("t1")));
						}

						clone.setStyle(newStyle);
						HIGHLIGHT_LAYER.getSource()?.addFeature(clone);
						features.current = [...features.current, feat];
					}
				};
				highlightPoint(feat);
				const props = feat.getProperties();
				if (feat instanceof RenderFeature || !feat?.get("internal")?.["style"]) {
					const layer = map.getAllLayers().find((l) => l.get("table") === props["internal"]["table"] && l.get("mapbox-source"));
					const line = FEATURE_LIST[(props["linked_to"] ?? props["kafka_id"] ?? props["id"]) + "-" + props["layer"] + "-LineString"];
					if (line) {
						const style = getLineHighlight();
						const clone = line.feature.clone();
						clone.setStyle(style);
						HIGHLIGHT_LAYER.getSource()?.addFeature(clone);
					}

					if (layer) {
						features.current = [...features.current, feat];
						setFeatureState(
							layer as any,
							{ id: (props["linked_to"] ?? props["kafka_id"] ?? props["id"]) + "-" + props["layer"] + "-LineString", source: props["layer"] },
							{ highlighted: true },
						);
						setFeatureState(
							layer as any,
							{ id: (props["linked_to"] ?? props["kafka_id"] ?? props["id"]) + "-" + props["layer"] + "-MultiLineString", source: props["layer"] },
							{ highlighted: true },
						);
					}
					if (feat.getGeometry() instanceof LineString) {
						const linkedFeature = FEATURE_LIST[props["linked_to"] + "-" + props["layer"] + "-Point"]?.feature;
						highlightPoint(linkedFeature);
					}
				}
			} else {
				removeCurrHighlight();
			}
		});

		return () => {
			map.un("pointermove", keyEvent.listener);
		};
	}, [map]);

	return <></>;
};
